import type { ReactNode } from 'react';
import { useI18n } from '../i18n/index.tsx';
import type { MessageKey } from '../i18n/messages.ts';
import type { Finding, PlanView } from '../api/types.ts';
import { Badge, Notice, type Tone } from '../components/ui.tsx';

const SEVERITY: Record<Finding['severity'], { tone: Tone; label: MessageKey }> = {
  blocker: { tone: 'danger', label: 'severity.blocker' },
  warning: { tone: 'warning', label: 'severity.warning' },
  info: { tone: 'info', label: 'severity.info' },
};

/**
 * The review screen.
 *
 * This is the screen the whole safety model exists for: everything that will
 * happen, in the customer's own language, before anything happens. The steps
 * are the real plan, the tunnel choice comes with the reason it was chosen,
 * and anything found on their servers is shown here rather than discovered
 * halfway through an install.
 */
export function ReviewStep({
  plan,
  findings,
}: {
  plan: PlanView;
  findings: readonly Finding[];
}): ReactNode {
  const { t, pick, formatNumber } = useI18n();
  const minutes = Math.max(1, Math.round(plan.estimatedSeconds / 60));
  const blockers = findings.filter((finding) => finding.severity === 'blocker');
  const others = findings.filter((finding) => finding.severity !== 'blocker');

  return (
    <div className="review">
      <div>
        <h2>{t('wizard.review.title')}</h2>
        <p className="review__summary">{pick(plan.summary)}</p>
        <p className="muted">
          {t('wizard.review.estimated', { minutes })} ·{' '}
          {t('wizard.review.steps', { count: plan.steps.length })}
        </p>
      </div>

      {plan.tunnel?.name ? (
        <section className="review__block">
          <h3>{t('wizard.review.tunnelChoice')}</h3>
          <p>
            <strong>{plan.tunnel.name}</strong>
          </p>
          {/* Why, not just what: a choice the customer cannot see the reason
              for is a choice they have to take on trust. */}
          {plan.tunnel.why ? <p className="muted">{pick(plan.tunnel.why)}</p> : null}
        </section>
      ) : null}

      {blockers.length > 0 ? (
        <section className="review__block">
          <Notice tone="danger" title={t('wizard.review.blocker')} />
          {blockers.map((finding, index) => (
            <FindingRow key={`${finding.code}-${index}`} finding={finding} />
          ))}
        </section>
      ) : null}

      {plan.warnings.length > 0 ? (
        <section className="review__block">
          <h3>{t('wizard.review.warnings')}</h3>
          {plan.warnings.map((warning, index) => (
            <Notice key={index} tone="warning">
              {pick(warning)}
            </Notice>
          ))}
        </section>
      ) : null}

      {others.length > 0 ? (
        <section className="review__block">
          <h3>{t('wizard.review.findings')}</h3>
          {others.map((finding, index) => (
            <FindingRow key={`${finding.code}-${index}`} finding={finding} />
          ))}
        </section>
      ) : null}

      <section className="review__block">
        <h3>{t('wizard.review.credentials')}</h3>
        <ul className="review__list">
          {plan.credentials.map((credential) => (
            <li key={credential.kind}>{pick(credential.label)}</li>
          ))}
        </ul>
      </section>

      {/* Folded, not removed.
          The promise this screen makes is that everything which will happen is
          visible before anything happens, so the steps stay — but twenty rows
          of "install Docker" above the approve button buries the summary, the
          warnings and the findings, which are the parts a customer can act on.
          One line, open in one click, and open by default for nobody: a person
          who wants the list knows they want it. */}
      <section className="review__block">
        <details className="review__plan">
          <summary>
            {t('wizard.review.stepsToggle')} ·{' '}
            <span className="muted">{t('wizard.review.steps', { count: plan.steps.length })}</span>
          </summary>
          <ol className="review__steps">
            {plan.steps.map((step) => (
              <li key={step.key}>
                <span className="review__step-number">{formatNumber(step.ordinal)}</span>
                <span>{pick(step.title)}</span>
              </li>
            ))}
          </ol>
        </details>
      </section>
    </div>
  );
}

function FindingRow({ finding }: { finding: Finding }): ReactNode {
  const { t, pick } = useI18n();
  const severity = SEVERITY[finding.severity];
  return (
    <Notice
      tone={severity.tone}
      title={
        <span className="review__finding-title">
          <Badge tone={severity.tone}>{t(severity.label)}</Badge>
          {pick(finding.title)}
          <span className="subtle">· {finding.serverName}</span>
        </span>
      }
    >
      {pick(finding.detail)}
    </Notice>
  );
}
